import { ABILITIES, abilityMod, proficiencyBonus, hpAverage } from './formulas';
import { computeWeaponAttacks } from './computeWeaponAttacks';
import { computeSkills, computePassivePerception } from './computeSkills';

// Compute derived stats from an assembled character draft. Pure function —
// every value it returns also carries a `breakdown` array describing every
// contributing source (P5: every number is a receipt).

const computeArmorClass = ({ abilities, equippedArmor, equippedShield, features }) => {
  const dex = abilityMod(abilities.dex);
  const breakdown = [];

  let base = 10;
  let maxDexBonus = Infinity;

  if (equippedArmor) {
    base = equippedArmor.baseAc || 10;
    breakdown.push({ source: `${equippedArmor.name} (base)`, value: base });
    if (equippedArmor.dexCap !== null && equippedArmor.dexCap !== undefined) {
      maxDexBonus = equippedArmor.dexCap;
    } else {
      // null cap = no limit (light armor / unarmored)
      maxDexBonus = Infinity;
    }
  } else {
    breakdown.push({ source: 'Unarmored base', value: 10 });
  }

  const dexBonus = Math.min(dex, maxDexBonus);
  if (dexBonus !== 0) {
    const note = dex !== dexBonus ? ` (capped from ${formatMod(dex)})` : '';
    breakdown.push({ source: `Dex modifier${note}`, value: dexBonus });
  }

  let shieldBonus = 0;
  if (equippedShield) {
    shieldBonus = equippedShield.acBonus || 2;
    breakdown.push({ source: `${equippedShield.name || 'Shield'}`, value: shieldBonus });
  }

  let styleBonus = 0;
  for (const feat of features || []) {
    const isDefense =
      feat.id === 'srd.feat.fighting-style.defense' ||
      feat.appliesFightingStyle === 'defense';
    if (isDefense && equippedArmor) {
      styleBonus += 1;
      breakdown.push({ source: 'Fighting Style: Defense', value: 1 });
    }
  }

  const total = base + dexBonus + shieldBonus + styleBonus;
  return { total, breakdown };
};

const formatMod = (mod) => (mod >= 0 ? `+${mod}` : `${mod}`);

// abilities can be a flat map (Phase 2 archetypes) OR a structured object
// (Phase 3+) with `{score, breakdown: [{source, value}]}`. Normalize to rows
// that always carry a breakdown so consumers get receipts regardless.
const computeAbilityRows = (abilities, abilityBreakdowns = null) =>
  ABILITIES.map((ab) => {
    const score = abilities[ab] || 0;
    const breakdown =
      (abilityBreakdowns && abilityBreakdowns[ab]) || [
        { source: 'Final score (archetype-baked)', value: score },
      ];
    return {
      ability: ab,
      score,
      mod: abilityMod(score),
      breakdown,
    };
  });

const computeSavingThrows = ({ abilities, saveProficiencies, pb }) => {
  const set = new Set(saveProficiencies || []);
  return ABILITIES.map((ab) => {
    const mod = abilityMod(abilities[ab] || 0);
    const proficient = set.has(ab);
    const total = mod + (proficient ? pb : 0);
    return { ability: ab, total, mod, proficient, breakdown: [{ source: `${ab.toUpperCase()} modifier`, value: mod }, ...(proficient ? [{ source: 'Proficiency Bonus', value: pb }] : [])] };
  });
};

const computeSpellStats = ({ spellcasting, abilities, pb }) => {
  if (!spellcasting?.ability) return null;
  const ability = spellcasting.ability;
  const abMod = abilityMod(abilities[ability] || 0);
  const saveDc = 8 + pb + abMod;
  const attackBonus = pb + abMod;
  return {
    ability,
    saveDc,
    attackBonus,
    breakdown: {
      saveDc: [
        { source: 'Base', value: 8 },
        { source: 'Proficiency Bonus', value: pb },
        { source: `${ability.toUpperCase()} modifier`, value: abMod },
      ],
      attackBonus: [
        { source: 'Proficiency Bonus', value: pb },
        { source: `${ability.toUpperCase()} modifier`, value: abMod },
      ],
    },
  };
};

// Phase 2.5: every derived value carries a `breakdown` (P5). Speed and PB
// are simple today but the structure means species speed-bonuses or feat
// PB-boosters land cleanly in Phase 3+ without refactoring callers.
// Phase 2.6: add weapon attacks, skill totals, passive perception, hit dice.
export const calculateDerivedStats = ({
  abilities,
  level,
  hitDie,
  speed,
  speedSources = [],
  saveProficiencies,
  skillProficiencies = [],
  expertiseSkills = [],
  spellcasting,
  equippedArmor,
  equippedShield,
  items = [],
  features,
}) => {
  const pb = proficiencyBonus(level);
  const conMod = abilityMod(abilities.con || 10);
  const dexMod = abilityMod(abilities.dex || 10);

  const hp = hpAverage(hitDie || 8, level || 1, conMod);
  const hpBreakdown = [
    { source: `Hit Die (d${hitDie}) max at L1`, value: hitDie },
    { source: 'Con modifier', value: conMod },
  ];
  if (level > 1) {
    hpBreakdown.push({
      source: `Average per level × ${level - 1}`,
      value: (Math.ceil(hitDie / 2) + 1 + conMod) * (level - 1),
    });
  }

  const ac = computeArmorClass({ abilities, equippedArmor, equippedShield, features });

  const initiativeBreakdown = [{ source: 'Dex modifier', value: dexMod }];
  // Future: Alert feat (+5), other inits

  const pbBreakdown = [
    { source: `Character level ${level || 1}`, value: pb },
  ];

  const speedBreakdown = [
    { source: 'Species base', value: speed || 30 },
    ...(speedSources || []),
  ];

  const spell = computeSpellStats({ spellcasting, abilities, pb });
  const saves = computeSavingThrows({ abilities, saveProficiencies, pb });
  const abilityRows = computeAbilityRows(abilities);

  const skillProfSet = new Set(skillProficiencies);
  const expertiseSet = new Set(expertiseSkills);

  const weaponAttacks = computeWeaponAttacks({ items, abilities, pb });
  const skills = computeSkills({
    abilities,
    pb,
    proficientSkills: skillProfSet,
    expertiseSkills: expertiseSet,
  });
  const passivePerception = computePassivePerception({
    abilities,
    pb,
    proficientSkills: skillProfSet,
  });

  // Hit dice pool: starting at level 1, the character has `level` hit dice of
  // their class hit-die size. Phase 3+ multiclass calls for a richer
  // shape with multi-source breakdown — kept as an array now so the swap is
  // additive (callers already read `display` + `breakdown`).
  const hitDice = {
    count: level || 1,
    sides: hitDie,
    display: `${level || 1}d${hitDie}`,
    breakdown: [{ source: `Class hit die × ${level || 1}`, value: `${level || 1}d${hitDie}` }],
  };

  return {
    proficiencyBonus: pb,
    pbBreakdown,
    hp,
    hpBreakdown,
    hitDice,
    ac: ac.total,
    acBreakdown: ac.breakdown,
    speed: speed || 30,
    speedBreakdown,
    initiative: dexMod,
    initiativeBreakdown,
    spell,
    saves,
    abilityRows,
    weaponAttacks,
    skills,
    passivePerception,
  };
};
